import { AxiosRequestConfig } from "axios";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import useRequest from "./useRequest";
import useCache from "./cache/useCache";
import { shouldRequest } from "src/core/api/callApi";

export const useResponse = <ResponseType>({ requestFunction, values, requestNothingOnNull = false, dependency, extraCacheKey = "", responseType, logLevel, }: { requestFunction: (...args: any[]) => AxiosRequestConfig<any>; values?: Record<string, any>; requestNothingOnNull?: boolean; dependency?: any[] | any; extraCacheKey?: string; responseType?: AxiosRequestConfig["responseType"]; logLevel?: string[]; }): { rows: ResponseType | null; setNewValue: Dispatch<SetStateAction<ResponseType | null>>; abort: () => void; loading: boolean; status: string | number | null; } => {
  const requestFunctionValues = Object.values(values ?? {});
  const originalRequest = requestFunction(...requestFunctionValues);

  if (responseType) originalRequest.responseType = responseType;

  const [result, setRequest, abort, loading, status] = useRequest({ logLevel });
  const response = (result?.Items as ResponseType) ?? (result as ResponseType);

  const [cachedResponse, setCachedResponse] = useCache<ResponseType>(originalRequest?.url + extraCacheKey,);

  const responseSigRef = useRef<string | null>(null);
  const computeSig = (v: any): string => {
    if (v === null || v === undefined) return String(v);
    if (typeof v !== "object") return String(v);
    try {
      return JSON.stringify(v);
    } catch {
      return Object.prototype.toString.call(v);
    }
  };

  useEffect(() => {
    if (response == null || responseType === "blob") return;
    const nextSig = computeSig(response);
    if (nextSig === responseSigRef.current) return;
    responseSigRef.current = nextSig;
    setCachedResponse(response);
  }, [response, setCachedResponse]);

  useEffect(() => {
    if (shouldRequest(requestNothingOnNull, requestFunctionValues)) {
      const request = requestFunction(...requestFunctionValues);
      if (responseType) request.responseType = responseType;
      setRequest(request);
    }
  }, [...(Array.isArray(dependency) ? dependency : [dependency])]);

  return { rows: responseType === "blob" ? (response ?? null) :
     (cachedResponse ?? null) as ResponseType | null, setNewValue: setCachedResponse, abort, loading, status, };
};

export default useResponse;

export interface KeyValuePair {
  Key?: number | string;
  Value?: string | number | any;
}
